import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Flag,
  Clock,
  CheckCircle,
  Search,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Ban,
  ShieldAlert,
  Eye,
  RefreshCw,
  Calendar,
  ChevronDown,
  Check,
  FileSpreadsheet
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../components/ui/button";
import AdminService from "../../sevices/admin.service";
import { toastSuccess, toastError } from "../../components/common/toastNotification";
import { fixPhotoUrl } from "../../lib/utils";
import SafeImage from "../../components/common/SafeImage";

const formatFullDate = (val) => {
  if (!val) return "—";
  let d;
  if (Array.isArray(val)) {
    const [y, mo, day, h = 0, mi = 0] = val;
    d = new Date(y, mo - 1, day, h, mi);
  } else {
    d = new Date(val);
  }
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
};

const TIME_RANGES = [
  { label: "Lifetime", value: "lifetime" },
  { label: "Today (24h)", value: "today" },
  { label: "Last 7 Days", value: "last7days" },
  { label: "Last 30 Days", value: "last30days" },
  { label: "Last Year", value: "lastyear" },
];

const STATUS_OPTIONS = [
  { label: "All Status", value: "All" },
  { label: "Pending", value: "Pending" },
  { label: "In Review", value: "In Review" },
  { label: "Resolved", value: "Resolved" },
  { label: "Dismissed", value: "Dismissed" },
];

const REASON_OPTIONS = [
  { label: "All Reasons", value: "All Reasons" },
  { label: "Inappropriate photos", value: "Inappropriate photos" },
  { label: "Harassment", value: "Harassment" },
  { label: "Spam / Scam", value: "Spam / Scam" },
  { label: "Hate speech", value: "Hate speech" },
  { label: "Fake profile", value: "Fake profile" },
  { label: "Inappropriate messages", value: "Inappropriate messages" },
  { label: "Underage user", value: "Underage user" },
  { label: "Other", value: "Other" },
];

const isDateInRange = (dateVal, rangeValue) => {
  if (!dateVal || rangeValue === "lifetime") return true;
  let d;
  if (Array.isArray(dateVal)) {
    const [y, mo, day, h = 0, mi = 0] = dateVal;
    d = new Date(y, mo - 1, day, h, mi);
  } else {
    d = new Date(dateVal);
  }
  if (isNaN(d.getTime())) return true;

  const now = new Date();
  const diffTime = now.getTime() - d.getTime();
  const diffDays = diffTime / (1000 * 3600 * 24);

  if (rangeValue === "today") return diffDays <= 1;
  if (rangeValue === "last7days") return diffDays <= 7;
  if (rangeValue === "last30days") return diffDays <= 30;
  if (rangeValue === "lastyear") return diffDays <= 365;
  return true;
};

/* ─── Custom Sleek Floating Date Filter Dropdown ────────── */
const DateFilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = TIME_RANGES.find((tr) => tr.value === value) || TIME_RANGES[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-white border border-purple-200 text-purple-700 hover:text-purple-900 text-xs font-semibold rounded-xl shadow-2xs hover:border-purple-300 hover:bg-purple-50/50 transition-all cursor-pointer"
      >
        <Calendar className="w-4 h-4 text-purple-600" />
        <span>{selectedOption.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-purple-500 transition-transform duration-200 ${isOpen ? "rotate-180 text-purple-700" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 sm:right-0 mt-1.5 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="px-3.5 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            TIMEFRAME RANGE
          </div>
          {TIME_RANGES.map((tr) => {
            const isSelected = tr.value === value;
            return (
              <button
                key={tr.value}
                type="button"
                onClick={() => {
                  onChange(tr.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3.5 py-2 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${isSelected
                  ? "bg-purple-50 text-purple-700 font-bold"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <span>{tr.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5 text-purple-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Custom Floating Dropdown Component ────────── */
const CustomSelectDropdown = ({ options, value, onChange, labelTitle, icon: Icon, fullWidth = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative text-left ${fullWidth ? "w-full" : "inline-block"}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`${fullWidth ? "w-full justify-between" : ""
          } inline-flex items-center gap-2 px-3.5 py-2.5 bg-white border border-gray-200 text-gray-700 hover:text-gray-900 text-xs font-semibold rounded-xl shadow-2xs hover:border-pink-300 hover:bg-pink-50/30 transition-all cursor-pointer`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-gray-500" />}
          <span>{selectedOption.label}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180 text-pink-600" : ""}`} />
      </button>

      {isOpen && (
        <div className={`absolute ${fullWidth ? "inset-x-0" : "right-0"} mt-1.5 min-w-[180px] bg-white rounded-2xl shadow-xl border border-gray-100 py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150 max-h-60 overflow-y-auto`}>
          {labelTitle && (
            <div className="px-3.5 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              {labelTitle}
            </div>
          )}
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3.5 py-2 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${isSelected
                  ? "bg-pink-50 text-pink-700 font-bold"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <span>{opt.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5 text-pink-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const s = (status || "PENDING").toUpperCase();
  const styles = {
    PENDING: "bg-amber-50 text-amber-700 border-amber-200",
    IN_REVIEW: "bg-blue-50 text-blue-700 border-blue-200",
    RESOLVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    DISMISSED: "bg-gray-100 text-gray-600 border-gray-200"
  };
  const labels = {
    PENDING: "Pending",
    IN_REVIEW: "In Review",
    RESOLVED: "Resolved",
    DISMISSED: "Dismissed"
  };
  return (
    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${styles[s] || styles.PENDING}`}>
      {labels[s] || s}
    </span>
  );
};

// Lightbox for proof attachments
const Lightbox = ({ url, onClose }) => {
  if (!url) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white hover:text-gray-300 cursor-pointer">
          <X className="w-6 h-6" />
        </button>
        <SafeImage
          src={url}
          alt="Attachment proof"
          className="max-w-full max-h-[80vh] rounded-xl object-contain shadow-2xl"
          fallback={
            <div className="p-12 text-center text-white/60 bg-white/5 rounded-2xl border border-white/10">
              <Eye className="w-12 h-12 mx-auto mb-2 text-white/40" />
              <p className="text-sm font-medium">Unable to preview attachment</p>
            </div>
          }
        />
        <a
          href={fixPhotoUrl(url)}
          target="_blank"
          rel="noreferrer"
          download
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold backdrop-blur-sm transition-colors"
        >
          <Download className="w-4 h-4" /> Download Original Image
        </a>
      </div>
    </div>
  );
};

// Interactive Export Modal System with Custom Sleek Dropdowns
const ReportExportModal = ({ isOpen, onClose, reports }) => {
  const [statusFilter, setStatusFilter] = useState("All");
  const [reasonFilter, setReasonFilter] = useState("All Reasons");
  const [timelineFilter, setTimelineFilter] = useState("lifetime");

  if (!isOpen) return null;

  const exportFilteredData = reports.filter((r) => {
    // Status
    if (statusFilter !== "All") {
      const targetStatus = statusFilter.toUpperCase().replace(/\s+/g, "_");
      if ((r.status || "").toUpperCase() !== targetStatus) return false;
    }
    // Reason
    if (reasonFilter !== "All Reasons") {
      if (!(r.reason || "").toLowerCase().includes(reasonFilter.toLowerCase())) return false;
    }
    // Timeline
    if (!isDateInRange(r.createdAt, timelineFilter)) return false;
    return true;
  });

  const handleDownload = () => {
    if (exportFilteredData.length === 0) {
      toastError("No reports match your export criteria.");
      return;
    }
    const headers = ["Report ID", "Reported User", "Reported Email", "Reported By", "Reporter Email", "Reason", "Details", "Status", "Reported On"];
    const rows = exportFilteredData.map((r) => [
      `RPT-${String(r.id).padStart(4, "0")}`,
      `"${r.reportedName || ""}"`,
      `"${r.reportedEmail || ""}"`,
      `"${r.reporterName || ""}"`,
      `"${r.reporterEmail || ""}"`,
      `"${r.reason || ""}"`,
      `"${(r.details || "").replace(/"/g, '""')}"`,
      r.status || "PENDING",
      `"${formatFullDate(r.createdAt)}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Signova_Reports_Export_${timelineFilter}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toastSuccess(`Exported ${exportFilteredData.length} report records!`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 relative">
        <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-pink-50 text-pink-600 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Export Reports Data</h3>
              <p className="text-xs text-gray-500">Configure parameters to generate customized report exports.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Custom Status Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Report Status</label>
            <CustomSelectDropdown
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              labelTitle="REPORT STATUS"
              fullWidth={true}
            />
          </div>

          {/* Custom Reason Category Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Topic / Reason Category</label>
            <CustomSelectDropdown
              options={REASON_OPTIONS}
              value={reasonFilter}
              onChange={(val) => setReasonFilter(val)}
              labelTitle="TOPIC REASON"
              fullWidth={true}
            />
          </div>

          {/* Custom Timeline Range Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Timeline Range</label>
            <CustomSelectDropdown
              options={TIME_RANGES}
              value={timelineFilter}
              onChange={(val) => setTimelineFilter(val)}
              labelTitle="TIMEFRAME RANGE"
              icon={Calendar}
              fullWidth={true}
            />
          </div>

          {/* Summary Preview Badge */}
          <div className="p-3.5 rounded-2xl bg-pink-50/50 border border-pink-100 flex items-center justify-between text-xs text-pink-900 font-medium mt-2">
            <span>Matching Reports:</span>
            <span className="font-bold bg-pink-100 px-2.5 py-0.5 rounded-full text-pink-700">{exportFilteredData.length} records</span>
          </div>
        </div>

        {/* Modal Buttons */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            className="px-5 py-2.5 rounded-xl text-xs font-semibold text-white transition-all shadow-md active:scale-95 flex items-center gap-2 cursor-pointer"
            style={{ backgroundColor: "#ec4899" }}
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>
    </div>
  );
};

// Side Slide-Over Drawer
const ReportDrawer = ({ report, onClose, onAction, actionLoading, onBanUser }) => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [showActionDropdown, setShowActionDropdown] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handler);
    };
  }, [onClose]);

  if (!report) return null;

  const reportIdFormatted = `#RPT-${String(report.id).padStart(4, "0")}`;
  const reportedPhoto = fixPhotoUrl(report.reportedPhoto);
  const reporterPhoto = fixPhotoUrl(report.reporterPhoto);
  const attachments = report.attachments || [];

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity" onClick={onClose} />

        {/* Drawer Content */}
        <div className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col z-10 overflow-hidden border-l border-gray-100 animate-in slide-in-from-right duration-300">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-bold text-gray-900">{reportIdFormatted}</h2>
              <StatusBadge status={report.status} />
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Reported User Section */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Reported User</p>
              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full overflow-hidden bg-pink-100 border border-pink-200 flex-shrink-0">
                    <SafeImage
                      src={reportedPhoto}
                      alt={report.reportedName || "Reported"}
                      className="w-full h-full object-cover"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center text-pink-600 font-bold text-base">
                          {report.reportedName?.charAt(0) || "U"}
                        </div>
                      }
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm leading-snug">{report.reportedName || "Anonymous"}</h3>
                    <p className="text-xs text-gray-500">
                      {report.reportedUserId ? `@${report.reportedUserId}` : report.reportedEmail || "—"}
                    </p>
                    {report.reportedJoinedAt && (
                      <p className="text-[11px] text-gray-400 mt-0.5">Joined {formatFullDate(report.reportedJoinedAt)}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    const targetId = report.reportedId || report.reportedUserId;
                    const path = targetId ? `/users?userId=${targetId}` : `/users?search=${encodeURIComponent(report.reportedEmail || '')}`;
                    navigate(path, {
                      state: {
                        user: {
                          id: targetId,
                          email: report.reportedEmail,
                          fullName: report.reportedName,
                          photoUrl1: report.reportedPhoto
                        },
                        returnReport: report,
                        from: "/reports"
                      }
                    });
                  }}
                  className="px-3 py-1.5 rounded-xl border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center gap-1 shadow-2xs cursor-pointer"
                >
                  View Profile
                </button>
              </div>
            </div>

            {/* Reason Section */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Reason</p>
              <div className="p-3.5 rounded-xl bg-red-50/60 border border-red-100">
                <p className="font-semibold text-red-950 text-sm">{report.reason || "Inappropriate Content"}</p>
                <p className="text-xs text-red-700 mt-1 leading-relaxed">
                  User was reported for violating Signova community standards.
                </p>
              </div>
            </div>

            {/* Reported By Section */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Reported By</p>
              <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-purple-100 border border-purple-200 flex-shrink-0">
                  <SafeImage
                    src={reporterPhoto}
                    alt={report.reporterName || "Reporter"}
                    className="w-full h-full object-cover"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center text-purple-600 font-bold text-sm">
                        {report.reporterName?.charAt(0) || "R"}
                      </div>
                    }
                  />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm leading-snug">{report.reporterName || "Anonymous Reporter"}</h4>
                  <p className="text-xs text-gray-500">
                    {report.reporterUserId ? `@${report.reporterUserId}` : report.reporterEmail || "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Details</p>
              <p className="text-xs text-gray-500 mb-2">Additional info provided by reporter.</p>
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 text-xs italic leading-relaxed relative">
                "{report.details || report.reason || "No additional text description provided by reporter."}"
              </div>
            </div>

            {/* Reported On */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Reported On</p>
              <p className="text-xs font-medium text-gray-800">{formatFullDate(report.createdAt)}</p>
            </div>

            {/* Attachments Section */}
            {attachments.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Attachments ({attachments.length})
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {attachments.map((imgUrl, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedImage(imgUrl)}
                      className="group relative rounded-xl border border-gray-200 overflow-hidden bg-gray-100 aspect-video cursor-pointer hover:shadow-md transition-all"
                    >
                      <SafeImage
                        src={imgUrl}
                        alt="Attachment"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        fallback={
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <Eye className="w-5 h-5" />
                          </div>
                        }
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Eye className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-5 border-t border-gray-100 bg-white space-y-2.5">
            <div className="relative">
              <button
                onClick={() => setShowActionDropdown((prev) => !prev)}
                disabled={!!actionLoading}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all shadow-md active:scale-[0.99] cursor-pointer"
                style={{ backgroundColor: "#ec4899" }}
              >
                <span>Take Action</span>
                <MoreVertical className="w-4 h-4" />
              </button>

              {showActionDropdown && (
                <div className="absolute bottom-full mb-2 inset-x-0 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-20 animate-in fade-in zoom-in-95 duration-150">
                  <button
                    onClick={() => {
                      setShowActionDropdown(false);
                      onBanUser(report.reportedId, report.reportedName);
                    }}
                    className="w-full px-4 py-2.5 text-left text-xs font-semibold text-red-600 hover:bg-red-50 flex items-center gap-2.5 cursor-pointer"
                  >
                    <Ban className="w-4 h-4" /> Ban Reported User
                  </button>
                  <button
                    onClick={() => {
                      setShowActionDropdown(false);
                      onAction(report.id, "IN_REVIEW");
                    }}
                    className="w-full px-4 py-2.5 text-left text-xs font-semibold text-blue-600 hover:bg-blue-50 flex items-center gap-2.5 cursor-pointer"
                  >
                    <ShieldAlert className="w-4 h-4" /> Mark as In Review
                  </button>
                </div>
              )}
            </div>

            {report.status !== "RESOLVED" && (
              <button
                disabled={!!actionLoading}
                onClick={() => onAction(report.id, "RESOLVED")}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-800 font-semibold text-sm transition-all shadow-2xs cursor-pointer"
              >
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                Mark as Resolved
              </button>
            )}

            {report.status !== "DISMISSED" && (
              <button
                disabled={!!actionLoading}
                onClick={() => onAction(report.id, "DISMISSED")}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-gray-400 hover:text-gray-600 font-medium text-xs transition-colors cursor-pointer"
              >
                Dismiss Report
              </button>
            )}
          </div>
        </div>
      </div>

      {selectedImage && <Lightbox url={selectedImage} onClose={() => setSelectedImage(null)} />}
    </>
  );
};

const Reports = () => {
  const location = useLocation();
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  // Synchronized Status Tab / Dropdown filter
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState("lifetime");
  const [reasonFilter, setReasonFilter] = useState("All Reasons");

  const [actionLoading, setActionLoading] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  useEffect(() => {
    if (location.state?.returnReport) {
      setSelectedReport(location.state.returnReport);
    }
  }, [location.state]);

  const fetchReports = useCallback(() => {
    setLoading(true);
    AdminService.getReports(0, 100, "all")
      .then((res) => {
        const data = res?.data?.data;
        const list = data?.content || [];
        setReports(list);
      })
      .catch(() => toastError("Failed to load reports"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleAction = async (reportId, status) => {
    setActionLoading(`${status}-${reportId}`);
    try {
      await AdminService.resolveReport(reportId, status);
      toastSuccess(`Report updated to ${status.toLowerCase().replace("_", " ")}`);
      fetchReports();
      window.dispatchEvent(new Event("refreshBadgeCounts"));
      setSelectedReport(null);
    } catch (err) {
      toastError(err?.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleBanUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to ban user ${userName || userId}?`)) return;
    setActionLoading(`ban-${userId}`);
    try {
      await AdminService.banUser(userId);
      toastSuccess(`User ${userName || userId} banned successfully.`);
      fetchReports();
      setSelectedReport(null);
    } catch {
      toastError("Failed to ban user");
    } finally {
      setActionLoading(null);
    }
  };

  // Filtered reports calculation
  const filteredReports = useMemo(() => {
    let result = reports.filter((r) => {
      // Status Filter
      if (statusFilter !== "All") {
        const targetStatus = statusFilter.toUpperCase().replace(/\s+/g, "_");
        if ((r.status || "").toUpperCase() !== targetStatus) return false;
      }
      // Search
      const query = searchQuery.toLowerCase().trim();
      if (query) {
        const matchReporter = (r.reporterName || "").toLowerCase().includes(query) || (r.reporterEmail || "").toLowerCase().includes(query);
        const matchReported = (r.reportedName || "").toLowerCase().includes(query) || (r.reportedEmail || "").toLowerCase().includes(query);
        const matchReason = (r.reason || "").toLowerCase().includes(query);
        const matchId = String(r.id).includes(query) || `rpt-${r.id}`.includes(query);
        if (!matchReporter && !matchReported && !matchReason && !matchId) return false;
      }
      // Reason filter
      if (reasonFilter !== "All Reasons") {
        if (!(r.reason || "").toLowerCase().includes(reasonFilter.toLowerCase())) return false;
      }
      // Date range filter
      if (!isDateInRange(r.createdAt, dateRange)) return false;

      return true;
    });

    return result;
  }, [reports, statusFilter, searchQuery, reasonFilter, dateRange]);

  const totalPages = Math.max(1, Math.ceil(filteredReports.length / 10));
  const safePage = Math.max(0, Math.min(page, totalPages - 1));

  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    let start = safePage - Math.floor(maxVisible / 2);
    if (start < 0) {
      start = 0;
    } else if (start + maxVisible > totalPages) {
      start = totalPages - maxVisible;
    }
    return Array.from({ length: maxVisible }, (_, i) => start + i);
  };

  // Paginated slice
  const paginatedReports = useMemo(() => {
    const start = safePage * 10;
    return filteredReports.slice(start, start + 10);
  }, [filteredReports, safePage]);

  // Metric counts
  const counts = useMemo(() => {
    const total = reports.length;
    const pending = reports.filter((r) => (r.status || "").toUpperCase() === "PENDING").length;
    const inReview = reports.filter((r) => (r.status || "").toUpperCase() === "IN_REVIEW").length;
    const resolved = reports.filter((r) => (r.status || "").toUpperCase() === "RESOLVED").length;
    const dismissed = reports.filter((r) => (r.status || "").toUpperCase() === "DISMISSED").length;

    return { total, pending, inReview, resolved, dismissed };
  }, [reports]);

  return (
    <>
      <div className="min-h-full bg-gray-50/50 p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Reports</h1>
            <p className="text-xs text-gray-500 mt-1">Review and take action on user reports to keep Signova safe.</p>
          </div>
          <button
            onClick={fetchReports}
            disabled={loading}
            className="self-start sm:self-auto px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-all flex items-center gap-2 shadow-2xs cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-500 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* 4 Analytics Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Reports */}
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Total Reports</p>
              <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
              <p className="text-[11px] text-gray-400 font-medium mt-1">Total reports submitted</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-pink-50 flex items-center justify-center text-pink-600">
              <Flag className="w-6 h-6" />
            </div>
          </div>

          {/* Pending Review */}
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Pending Review</p>
              <p className="text-2xl font-bold text-gray-900">{counts.pending}</p>
              <p className="text-[11px] text-amber-600 font-medium mt-1">Requires admin attention</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600">
              <Clock className="w-6 h-6" />
            </div>
          </div>

          {/* In Review */}
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">In Review</p>
              <p className="text-2xl font-bold text-gray-900">{counts.inReview}</p>
              <p className="text-[11px] text-purple-600 font-medium mt-1">Currently investigating</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600">
              <Eye className="w-6 h-6" />
            </div>
          </div>

          {/* Resolved */}
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">{counts.resolved}</p>
              <p className="text-[11px] text-emerald-600 font-medium mt-1">Action completed cases</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Search, Filters, and Tab Pills Container */}
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs space-y-4">
          {/* Top Control Bar */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            {/* Search Input & Custom Date Dropdown beside Search Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1">
              {/* Search Input */}
              <div className="relative flex-1 min-w-[240px]">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search reports by user, reported user or reason..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-all bg-gray-50/50"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Custom Sleek Date Range Dropdown matching Users.js */}
              <DateFilterDropdown
                value={dateRange}
                onChange={(val) => setDateRange(val)}
              />
            </div>

            {/* Filter Dropdowns & Export Modal Trigger */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Custom Status Dropdown (Synchronized with Tab Pills) */}
              <CustomSelectDropdown
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(val);
                  setPage(0);
                }}
                labelTitle="STATUS FILTER"
              />

              {/* Custom Reasons Dropdown */}
              <CustomSelectDropdown
                options={REASON_OPTIONS}
                value={reasonFilter}
                onChange={(val) => setReasonFilter(val)}
                labelTitle="TOPIC REASON"
              />

              {/* Export Button (Opens Export Modal Popup) */}
              <button
                onClick={() => setIsExportModalOpen(true)}
                className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 flex items-center gap-2 transition-all shadow-2xs cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-gray-500" />
                Export
              </button>
            </div>
          </div>

          {/* Status Tabs with Fixed Always-Visible Badges (like Verification.js) */}
          <div className="flex items-center gap-2 border-t border-gray-100 pt-3 overflow-x-auto">
            {[
              { id: "All", label: "All Reports", count: counts.total },
              { id: "Pending", label: "Pending", count: counts.pending },
              { id: "In Review", label: "In Review", count: counts.inReview },
              { id: "Resolved", label: "Resolved", count: counts.resolved },
              { id: "Dismissed", label: "Dismissed", count: counts.dismissed },
            ].map((tab) => {
              const active = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setStatusFilter(tab.id);
                    setPage(0);
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${active
                    ? "bg-pink-50 text-pink-700 shadow-2xs"
                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                >
                  <span>{tab.label}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active
                      ? "bg-pink-200/60 text-pink-800"
                      : "bg-gray-100 text-gray-600"
                      }`}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reports Table Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-xs overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-gray-400 uppercase tracking-wider font-semibold text-[11px]">
                  <th className="px-6 py-4">Report ID</th>
                  <th className="px-4 py-4">Reported User</th>
                  <th className="px-4 py-4">Reported By</th>
                  <th className="px-4 py-4">Reason</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4">Reported On</th>
                  <th className="px-6 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 7 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-100 rounded animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-9 w-36 bg-gray-100 rounded-full animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-9 w-36 bg-gray-100 rounded-full animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-4 w-28 bg-gray-100 rounded animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-4 w-24 bg-gray-100 rounded animate-pulse" /></td>
                      <td className="px-6 py-4 text-right"><div className="h-6 w-6 bg-gray-100 rounded-full animate-pulse ml-auto" /></td>
                    </tr>
                  ))
                ) : paginatedReports.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <Flag className="w-8 h-8 text-gray-300" />
                        <p className="text-sm font-semibold text-gray-600">No reports found</p>
                        <p className="text-xs text-gray-400">Try adjusting your filters or search query.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedReports.map((r) => {
                    const reportIdFormatted = `#RPT-${String(r.id).padStart(4, "0")}`;
                    const reportedPhoto = fixPhotoUrl(r.reportedPhoto);
                    const reporterPhoto = fixPhotoUrl(r.reporterPhoto);

                    return (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedReport(r)}
                        className="hover:bg-gray-50/80 transition-colors cursor-pointer group"
                      >
                        {/* Report ID */}
                        <td className="px-6 py-4 font-semibold text-gray-900 text-xs">
                          {reportIdFormatted}
                        </td>

                        {/* Reported User */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-pink-100 border border-pink-200 flex-shrink-0">
                              <SafeImage
                                src={reportedPhoto}
                                alt={r.reportedName || "User"}
                                className="w-full h-full object-cover"
                                fallback={
                                  <div className="w-full h-full flex items-center justify-center text-pink-600 font-bold text-xs">
                                    {r.reportedName?.charAt(0) || "U"}
                                  </div>
                                }
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 leading-tight truncate max-w-[140px]">
                                {r.reportedName || "Anonymous"}
                              </p>
                              <p className="text-[11px] text-gray-400 truncate max-w-[140px]">
                                {r.reportedUserId ? `@${r.reportedUserId}` : r.reportedEmail || "—"}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Reported By */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-purple-100 border border-purple-200 flex-shrink-0">
                              <SafeImage
                                src={reporterPhoto}
                                alt={r.reporterName || "Reporter"}
                                className="w-full h-full object-cover"
                                fallback={
                                  <div className="w-full h-full flex items-center justify-center text-purple-600 font-bold text-xs">
                                    {r.reporterName?.charAt(0) || "R"}
                                  </div>
                                }
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 leading-tight truncate max-w-[140px]">
                                {r.reporterName || "Reporter"}
                              </p>
                              <p className="text-[11px] text-gray-400 truncate max-w-[140px]">
                                {r.reporterUserId ? `@${r.reporterUserId}` : r.reporterEmail || "—"}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Reason */}
                        <td className="px-4 py-4 font-medium text-gray-700 max-w-[180px] truncate">
                          {r.reason || "—"}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-4">
                          <StatusBadge status={r.status} />
                        </td>

                        {/* Reported On */}
                        <td className="px-4 py-4 text-gray-500 font-normal">
                          {formatFullDate(r.createdAt)}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setSelectedReport(r)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
              <p className="text-xs text-gray-500">
                Showing <span className="font-semibold text-gray-900">{filteredReports.length > 0 ? safePage * 10 + 1 : 0}</span> to{" "}
                <span className="font-semibold text-gray-900">
                  {Math.min((safePage + 1) * 10, filteredReports.length)}
                </span>{" "}
                of <span className="font-semibold text-gray-900">{filteredReports.length}</span> reports
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="rounded-xl border-gray-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {getVisiblePageNumbers().map((idx) => (
                  <button
                    key={idx}
                    onClick={() => setPage(idx)}
                    className={`w-8 h-8 rounded-xl text-xs font-semibold transition-all cursor-pointer ${safePage === idx
                      ? "bg-pink-600 text-white shadow-2xs"
                      : "text-gray-600 hover:bg-gray-100"
                      }`}
                  >
                    {idx + 1}
                  </button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={safePage >= totalPages - 1}
                  className="rounded-xl border-gray-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Side Slide-Over Drawer */}
      {selectedReport && (
        <ReportDrawer
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          onAction={handleAction}
          onBanUser={handleBanUser}
          actionLoading={actionLoading}
        />
      )}

      {/* Export Modal Popup */}
      <ReportExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        reports={reports}
      />
    </>
  );
};

export default Reports;
